import * as React from "react";

import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";

import {
  CHUNK_LOAD_ERROR_MESSAGE,
  isChunkLoadError
} from "utils/lazy-with-retry.util";

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

interface RouteErrorBoundaryProps {
  children: React.ReactNode;
  routeName?: string;
}

export class RouteErrorBoundary extends React.Component<
  RouteErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: RouteErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("Route loading error:", error, errorInfo);
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const isChunkError = isChunkLoadError(this.state.error);
      return (
        <Container>
          <Box sx={{ mt: 4 }}>
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="h5" gutterBottom>
                Failed to load {this.props.routeName || "this page"}
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {isChunkError
                  ? CHUNK_LOAD_ERROR_MESSAGE
                  : this.state.error?.message ||
                    "An unexpected error occurred while loading this page."}
              </Typography>
            </Alert>
            <Button variant="contained" onClick={this.handleReload}>
              {isChunkError ? "Retry" : "Reload Page"}
            </Button>
          </Box>
        </Container>
      );
    }

    return this.props.children;
  }
}
